import React, { Suspense, useState } from "react";
import { SECTION_COMPONENTS } from "./sections/components";
import { SECTION_DEFINITIONS } from "./themeRegistry";

// Space kept while a section's code downloads, so the page does not jump.
const LOADING_HEIGHT = { announcement_bar: 36, marquee: 44, hero_banner: 460, image_banner: 320, trust_strip: 96 };

export default function SectionRenderer({
  sections = [],
  themeKey = "prestige",
  globalSettings = {},
  categories = [],
  products = [],
  banners = [],
  storeSettings = null,
  onAddToCart,
  productsReady = true,
  selectedSectionId = null,
  onSelectSection = null,
  isEditor = false,
  previewSlideId = null,
  viewportMode = "desktop",
}) {
  const [hoveredId, setHoveredId] = useState(null);
  const sectionName = (type) => {
    const def = SECTION_DEFINITIONS.find((item) => item.type === type);
    return def?.name || String(type || "Section").replace(/_/g, " ");
  };

  return (
    <div
      className={`theme-storefront-wrapper theme-${themeKey}`}
      style={{
        backgroundColor: globalSettings?.backgroundColor || "#ffffff",
        color: globalSettings?.textColor || "#111827",
        fontFamily: globalSettings?.bodyFontFamily || "'Inter', sans-serif",
        "--showcase-accent": globalSettings?.accentColor || "#FF6900",
        "--showcase-ink": globalSettings?.textColor || "#17140F",
        "--showcase-muted": globalSettings?.mutedTextColor || "#6B655C",
        "--showcase-surface": globalSettings?.surfaceColor || "#ffffff",
      }}
    >
      {sections.map((section, index) => {
        if (section.enabled === false && !isEditor) {
          return null;
        }

        const isSelected = selectedSectionId === section.id;
        const isPrompt = isEditor && hoveredId === section.id && !isSelected;
        const name = sectionName(section.type);

        // Unknown types (e.g. from a theme made with newer sections) are skipped.
        const SectionComponent = SECTION_COMPONENTS[section.type];
        if (!SectionComponent) return null;

        return (
          <div
            key={section.id || index}
            id={section.id}
            onClick={isEditor && onSelectSection ? (e) => {
              const link = e.target.closest && e.target.closest("a");
              if (link) e.preventDefault();
              e.stopPropagation();
              onSelectSection(section.id);
            } : undefined}
            onMouseEnter={isEditor ? () => setHoveredId(section.id) : undefined}
            onMouseLeave={isEditor ? () => setHoveredId(null) : undefined}
            className={`theme-section-wrapper position-relative ${
              isEditor ? "cursor-pointer" : ""
            } ${isSelected ? "theme-section-selected" : ""} ${isPrompt ? "theme-section-prompt" : ""}`}
            style={{
              opacity: section.enabled === false && isEditor ? 0.45 : 1,
            }}
          >
            {isEditor && isSelected && (
              <div className="theme-section-editing">
                Editing: {name}
              </div>
            )}
            {isPrompt && <div className="theme-section-prompt-label">{name}</div>}
            <Suspense fallback={<div className="theme-section-loading" aria-hidden="true" style={{ minHeight: LOADING_HEIGHT[section.type] || 160 }} />}>
            <SectionComponent
              settings={section.settings || {}}
              themeKey={themeKey}
              globalSettings={globalSettings}
              categories={categories}
              products={products}
              banners={banners}
              storeSettings={storeSettings}
              onAddToCart={onAddToCart}
              productsReady={productsReady}
              isEditor={isEditor}
              viewportMode={isEditor ? viewportMode : "desktop"}
              previewSlideId={isEditor ? previewSlideId : null}
            />
            </Suspense>
          </div>
        );
      })}
    </div>
  );
}
